import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_FORMATS_LABEL,
  MAX_ATTACHMENT_SIZE_MB,
} from '../../utils/attachmentFiles';
import {
  CONTRACT_NAME_MAX,
  type ContractWizardDraft,
} from './useContractWizardDraft';
import { InfoTip } from './InfoTip';
import { shortFileName } from '../../utils/fileName';

// Same toolbar as the contract page's description editors, so the stored HTML
// (and what the customer signs) looks the same whichever editor produced it.
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    ['link'],
    ['clean'],
  ],
};
const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'color', 'background', 'link'];

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;

interface AssignmentStepProps {
  draft: ContractWizardDraft;
  stepCounter: string;
  variation?: { inheritedTerms: string };
}

/** Wizard step 1 — "Uppdrag & filer". Controlled by `useContractWizardDraft`. */
export function AssignmentStep({ draft, stepCounter, variation }: AssignmentStepProps) {
  const { t } = useTranslation();
  const ids = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const { values, setField, errors, showErrors, attachments } = draft;
  const show = (flag?: boolean) => showErrors && !!flag;

  const onReferToggle = (checked: boolean) => {
    if (checked && attachments.length === 0) return;
    draft.setFieldAndSave('referToAttachments', checked);
  };

  const onFiles = (list: FileList | null) => {
    if (list && list.length > 0) void draft.uploadFiles(Array.from(list));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="contract-wizard-step">
      <header className="mb-4">
        <p className="contract-wizard-kicker mb-1">{stepCounter}</p>
        <h2 className="contract-wizard-step-title">{t(variation ? 'variationWizard.assignmentTitle' : 'contracts.wizard.steps.assignment.title')}</h2>
        <p className="text-muted mb-0">{t(variation ? 'variationWizard.assignmentIntro' : 'contracts.wizard.assignment.intro')}</p>
      </header>

      {showErrors && (errors.contractName || errors.assignment) && (
        <div className="alert alert-danger d-flex gap-2" role="alert">
          <i className="feather-alert-triangle mt-1" aria-hidden="true"></i>
          <span>
            <strong>{t('contracts.descriptions.requiredModalTitle')}</strong>{' '}
            {t('contracts.wizard.assignment.errorBanner')}
          </span>
        </div>
      )}

      <section aria-labelledby={`${ids}-assignment`} className="mb-4">
        <h3 id={`${ids}-assignment`} className="contract-wizard-section-title">
          {t('contracts.descriptions.title')}
        </h3>

        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 position-relative mb-1">
            <label htmlFor={`${ids}-name`} className="form-label fw-semibold mb-0">
              {t('contracts.descriptions.contractNameTitle')} <span className="text-danger" aria-hidden="true">*</span>
            </label>
            <InfoTip label={t('contracts.descriptions.moreInfoAria')}>
              {t('contracts.descriptions.tooltips.contractName')}
              <em>{t('contracts.descriptions.tooltips.contractNameExample')}</em>
              {t('contracts.descriptions.tooltips.contractNameSuffix')}
            </InfoTip>
          </div>
          <input
            id={`${ids}-name`}
            type="text"
            data-autofocus
            className={`form-control${show(errors.contractName) ? ' is-invalid' : ''}`}
            value={values.contractName}
            maxLength={CONTRACT_NAME_MAX}
            placeholder={t('contracts.descriptions.contractNamePlaceholder')}
            onChange={event => setField('contractName', event.target.value.slice(0, CONTRACT_NAME_MAX))}
            onBlur={draft.autosave}
            aria-required="true"
            aria-invalid={show(errors.contractName) || undefined}
            aria-describedby={`${ids}-name-help`}
          />
          {show(errors.contractName) && (
            <div className="invalid-feedback d-block">{t('contracts.descriptions.contractNameRequired')}</div>
          )}
          <div id={`${ids}-name-help`} className="form-text d-flex justify-content-between gap-3">
            <span>{t('contracts.wizard.assignment.contractNameHelp')}</span>
            <span>{t('contracts.wizard.assignment.counter', { count: values.contractName.length, max: CONTRACT_NAME_MAX })}</span>
          </div>
        </div>

        {variation && <fieldset className="mb-3">
          <legend className="form-label fw-semibold fs-6">{t('variationWizard.reason')}</legend>
          <div className="d-flex flex-wrap gap-2">
            {['customer_request', 'unforeseen', 'authority', 'dispute'].map(reason => <label key={reason} className={`btn variation-wizard-touch ${values.variationReason === reason ? 'btn-primary' : 'btn-outline-secondary'}`}>
              <input type="radio" name={`${ids}-reason`} className="visually-hidden" checked={values.variationReason === reason} onChange={() => draft.setFieldAndSave('variationReason', reason)} />
              {t(`variationWizard.reasons.${reason}`)}
            </label>)}
          </div>
        </fieldset>}

        {!variation && <div className="contract-wizard-choice mb-3">
          <p className="fw-bold mb-1">{t('contracts.descriptions.tooltips.assignmentChooseHeading')}</p>
          <p className="mb-1">
            <strong>{t('contracts.descriptions.tooltips.assignmentOption1Label')}</strong>
            {t('contracts.descriptions.tooltips.assignmentOption1Text')}
          </p>
          <p className="mb-0">
            <strong>{t('contracts.descriptions.tooltips.assignmentOption2Label')}</strong>
            {t('contracts.descriptions.tooltips.assignmentOption2Text')}
          </p>
        </div>}

        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 position-relative mb-1">
            <span id={`${ids}-assign-label`} className="form-label fw-semibold mb-0">
              {t(variation ? 'variationWizard.description' : 'contracts.descriptions.assignmentTitle')}{' '}
              {values.referToAttachments ? (
                <span className="fw-normal text-muted">{t('contracts.wizard.assignment.assignmentOptional')}</span>
              ) : (
                <span className="text-danger" aria-hidden="true">*</span>
              )}
            </span>
            <InfoTip label={t('contracts.descriptions.moreInfoAria')}>
              <span className="d-block mb-2">{t('contracts.descriptions.tooltips.assignmentIntro')}</span>
              <span className="d-block fw-bold mb-1">{t('contracts.descriptions.tooltips.assignmentChooseHeading')}</span>
              <span className="d-block mb-1">
                <strong>{t('contracts.descriptions.tooltips.assignmentOption1Label')}</strong>
                {t('contracts.descriptions.tooltips.assignmentOption1Text')}
              </span>
              <span className="d-block mb-2">
                <strong>{t('contracts.descriptions.tooltips.assignmentOption2Label')}</strong>
                {t('contracts.descriptions.tooltips.assignmentOption2Text')}
              </span>
              <hr />
              <span className="d-block">
                <strong className="etappio-info-tip__important">{t('contracts.descriptions.tooltips.assignmentImportantLabel')}</strong>
                {t('contracts.descriptions.tooltips.assignmentImportantText')}
              </span>
            </InfoTip>
          </div>
          <div
            className={`contract-wizard-editor${show(errors.assignment) ? ' is-invalid' : ''}`}
            aria-labelledby={`${ids}-assign-label`}
            role="group"
            onBlur={draft.autosave}
          >
            <ReactQuill
              theme="snow"
              className="contract-description-quill"
              value={values.assignmentDescription}
              onChange={(value, _delta, source) => {
                if (source === 'user') setField('assignmentDescription', value);
              }}
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
          {show(errors.assignment) && (
            <div className="invalid-feedback d-block">{t('contracts.descriptions.requiredAssignmentItem')}</div>
          )}
        </div>

        <div className="mb-3">
          <div className="form-check contract-wizard-check">
            <input
              id={`${ids}-refer`}
              type="checkbox"
              className="form-check-input"
              checked={values.referToAttachments}
              disabled={attachments.length === 0 && !values.referToAttachments}
              onChange={event => onReferToggle(event.target.checked)}
            />
            <label className="form-check-label" htmlFor={`${ids}-refer`}>
              {t('contracts.descriptions.referToAttachmentsLabel')}
            </label>
          </div>
          {attachments.length === 0 && (
            <p className="form-text mb-0">{t('contracts.descriptions.referToAttachmentsNoFiles')}</p>
          )}
          {values.referToAttachments && (
            <div className="alert alert-warning d-flex gap-2 mt-2 mb-0" role="note">
              <i className="feather-alert-triangle mt-1" aria-hidden="true"></i>
              <span>
                <strong>{t('contracts.descriptions.tooltips.assignmentImportantLabel')}</strong>
                {t('contracts.descriptions.tooltips.assignmentImportantText')}
              </span>
            </div>
          )}
        </div>

        {variation && <details className="border rounded p-3 mb-3">
          <summary className="fw-semibold variation-wizard-touch">{t('variationWizard.inheritedTerms')}</summary>
          {variation.inheritedTerms ? <div className="mt-2 contract-description-rendered" dangerouslySetInnerHTML={{ __html: variation.inheritedTerms }} /> : <p className="text-muted mb-0 mt-2">{t('variationWizard.noInheritedTerms')}</p>}
        </details>}
        <div className="mb-1">
          <div className="d-flex align-items-center gap-2 position-relative mb-1">
            <span id={`${ids}-other-label`} className="form-label fw-semibold mb-0">
              {t(variation ? 'variationWizard.additionalTerms' : 'contracts.descriptions.otherTitle')}{' '}
              <span className="fw-normal text-muted">{t('contracts.descriptions.otherOptional')}</span>
            </span>
            <InfoTip label={t('contracts.descriptions.moreInfoAria')}>
              {t('contracts.descriptions.tooltips.other')}
            </InfoTip>
          </div>
          <div className="contract-wizard-editor" aria-labelledby={`${ids}-other-label`} role="group" onBlur={draft.autosave}>
            <ReactQuill
              theme="snow"
              className="contract-description-quill"
              value={values.otherDescription}
              onChange={(value, _delta, source) => {
                if (source === 'user') setField('otherDescription', value);
              }}
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
        </div>
      </section>

      <hr className="contract-wizard-divider" />

      <section aria-labelledby={`${ids}-files`}>
        <div className="d-flex justify-content-between align-items-baseline gap-3 mb-3">
          <h3 id={`${ids}-files`} className="contract-wizard-section-title mb-0">
            {t('contracts.attachments.contractFilesTitle')}
          </h3>
          <span className="small text-muted">{t('contracts.wizard.files.count', { count: attachments.length })}</span>
        </div>

        <div
          className={`contract-wizard-dropzone${dragOver ? ' is-active' : ''}`}
          onDragOver={event => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={event => {
            event.preventDefault();
            setDragOver(false);
            onFiles(event.dataTransfer.files);
          }}
        >
          <span className="contract-wizard-dropzone-icon" aria-hidden="true">
            <i className="feather-upload"></i>
          </span>
          <p className="fw-semibold mb-0">{t('contracts.wizard.files.dropHint')}</p>
          <div className="d-flex flex-column flex-md-row gap-2 w-100 justify-content-center">
            <button
              type="button"
              className="btn contract-wizard-btn-outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={draft.uploading}
            >
              {draft.uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                  {t('contracts.attachments.uploading')}
                </>
              ) : (
                <>
                  <i className="feather-upload me-2" aria-hidden="true"></i>
                  {t('contracts.attachments.upload')}
                </>
              )}
            </button>
            <button type="button" className="btn btn-link contract-wizard-link" onClick={() => void draft.openLibrary()}>
              <i className="feather-folder me-2" aria-hidden="true"></i>
              {t('contracts.attachments.fromLibrary')}
            </button>
          </div>
          <p className="small text-muted mb-0">
            {t('contracts.attachments.formatsHint', { formats: ATTACHMENT_FORMATS_LABEL, max: MAX_ATTACHMENT_SIZE_MB })}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            className="d-none"
            multiple
            accept={ATTACHMENT_ACCEPT}
            onChange={event => onFiles(event.target.files)}
            aria-label={t('contracts.attachments.upload')}
          />
        </div>

        {draft.fileMessage && (
          <div className={`alert ${draft.fileMessage.type === 'error' ? 'alert-danger' : 'alert-success'} mt-3 mb-0`} role="alert">
            {draft.fileMessage.text}
          </div>
        )}

        {attachments.length === 0 ? (
          <p className="text-muted mt-3 mb-0">{t('contracts.attachments.noAttachments')}</p>
        ) : (
          <ul className="list-unstyled d-flex flex-column gap-2 mt-3 mb-0">
            {attachments.map(doc => (
              <li key={doc.id} className="contract-wizard-file">
                <span className="contract-wizard-file-icon" aria-hidden="true">
                  <i className="feather-file-text"></i>
                </span>
                <span className="contract-wizard-file-name">
                  <span className="d-block text-truncate fw-medium" title={doc.originalName || doc.fileName}>
                    {shortFileName(doc.originalName || doc.fileName)}
                  </span>
                  <span className="d-block small text-muted">{formatSize(doc.size)}</span>
                </span>
                <button
                  type="button"
                  className="btn btn-link contract-wizard-icon-btn"
                  onClick={() => void draft.removeAttachment(doc)}
                  aria-label={t('contracts.wizard.files.removeAria', { name: doc.originalName || doc.fileName })}
                >
                  <i className="feather-trash-2" aria-hidden="true"></i>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default AssignmentStep;
